import { PropsWithChildren, useState } from "react";

import { IconNavigationChevronDown } from "@/assets/icons/figma/IconNavigationChevronDown";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";

type FacetSectionProps = PropsWithChildren<{
  title: string;
  /** Facet key, reported when the section is opened. Omit to report nothing. */
  filterId?: string;
  /** Number of active selections, shown as a badge and forces the section open. */
  selectedCount?: number;
  defaultOpen?: boolean;
}>;

/** Collapsible filter section. Opens by default when it has active selections. */
export const FacetSection = ({
  title,
  filterId,
  selectedCount = 0,
  defaultOpen,
  children,
}: FacetSectionProps) => {
  const [open, setOpen] = useState(defaultOpen ?? selectedCount > 0);

  /* Opening only, never closing — collapsing a section says nothing about intent, and the
   * gateway PLP ignored it too. A section that starts open (because it has selections) reports
   * nothing until someone actually opens it themselves. */
  const toggle = () => {
    /* Read off state rather than from inside the updater: React may run an updater twice in
     * development, which would report the same expand twice. */
    if (!open) {
      analyticsService.trackFilterExpandList(filterId);
    }

    setOpen(isOpen => !isOpen);
  };

  return (
    <section className="facet-section">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="facet-section-trigger"
      >
        <span className="facet-section-title">
          {/* Wrapped: `space-x` spaces element children, and a bare text node would get none. */}
          <span>{title}</span>
          {selectedCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1 text-xs font-bold text-white">
              {selectedCount}
            </span>
          )}
        </span>
        <IconNavigationChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-grey-500 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && <div className="pt-3">{children}</div>}
    </section>
  );
};
